import * as React from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { Separator } from "@/shadcn/components/ui/separator"
import { SourceMark } from "./marks"
import { type SourceCardSpec, type SourceNetwork } from "./types"

/**
 * Step 1 · choose a source.
 *
 * Ten sources: four featured cards and six behind "More sources". The set is
 * 1:1 with prod's ChooseFeedType tiles, so the caller maps `network` back to
 * its own input type/subtype codes rather than this file knowing them.
 *
 * A card is a real button only when `onSelect` is passed; without it the step
 * renders as inert markup, which is what the design harness shows.
 */

/**
 * The ten sources, as prod offers them: four featured and six behind the
 * disclosure.
 *
 * Product data rather than sample data — this list is 1:1 with the tiles
 * ChooseFeedType renders (same networks, labels, sublabels and order), and the
 * caller maps each `network` back to its own input type/subtype codes. Adding a
 * source means adding it here AND to that map.
 */
export const PRIMARY_SOURCES: SourceCardSpec[] = [
  { network: "rss", label: "RSS Feed", sublabel: "Website / Blog" },
  { network: "instagram", label: "Instagram", sublabel: "Professional account" },
  { network: "facebook", label: "Facebook", sublabel: "Page" },
  { network: "tiktok", label: "TikTok", sublabel: "Profile" },
]

export const MORE_SOURCES: SourceCardSpec[] = [
  { network: "youtube", label: "YouTube" },
  { network: "pinterest", label: "Pinterest" },
  { network: "wordpress", label: "WordPress" },
  { network: "etsy", label: "Etsy" },
  { network: "shopify", label: "Shopify" },
  { network: "substack", label: "Substack" },
]

/**
 * Interactive cards need the affordances the drawn frame doesn't show — hover,
 * focus ring, pointer. Shared so the two card sizes can't drift apart.
 */
const CARD_BASE =
  "tw:flex tw:flex-col tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-border tw:bg-card tw:shadow-xs"
const CARD_INTERACTIVE =
  "tw:cursor-pointer tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"

function SourceCard({
  spec,
  base,
  onSelect,
}: {
  spec: SourceCardSpec
  base: string
  onSelect?: (network: SourceNetwork) => void
}) {
  // min-h and px are Figma desktop values, where the card is ~220 wide and 196
  // tall reads square. At mobile it's ~144 wide, so the height only enforces a
  // portrait box and the 20px sides wrap every sublabel.
  const content = (
    <>
      <SourceMark network={spec.network} base={base} />
      <p className="tw:text-base tw:font-medium tw:text-foreground">
        {spec.label}
      </p>
      <Separator />
      {spec.sublabel ? (
        <p className="tw:text-center tw:text-sm tw:font-medium tw:text-muted-foreground">
          {spec.sublabel}
        </p>
      ) : null}
    </>
  )
  const shape =
    "tw:min-h-[150px] tw:flex-1 tw:gap-3.5 tw:px-3 tw:py-6 tw:md:min-h-[196px] tw:md:px-5"

  if (!onSelect) {
    return <div className={cn(CARD_BASE, shape)}>{content}</div>
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(spec.network)}
      className={cn(CARD_BASE, CARD_INTERACTIVE, shape)}
    >
      {content}
    </button>
  )
}

function MoreSourceCard({
  spec,
  base,
  onSelect,
}: {
  spec: SourceCardSpec
  base: string
  onSelect?: (network: SourceNetwork) => void
}) {
  // px steps down at mobile: at 3-up the tile is ~93 wide, and 16px sides leave
  // 61px of content — narrower than "WordPress", which then overruns them.
  const content = (
    <>
      <SourceMark
        network={spec.network}
        base={base}
        size="tw:size-7"
        fontSize="tw:text-[26px]"
      />
      <p className="tw:text-sm tw:font-medium tw:text-foreground">
        {spec.label}
      </p>
    </>
  )
  const shape =
    "tw:min-h-[92px] tw:flex-1 tw:gap-2.5 tw:px-2 tw:py-4 tw:md:px-4"

  if (!onSelect) {
    return <div className={cn(CARD_BASE, shape)}>{content}</div>
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(spec.network)}
      className={cn(CARD_BASE, CARD_INTERACTIVE, shape)}
    >
      {content}
    </button>
  )
}

export function SourceStep({
  base,
  primarySources,
  moreSources,
  moreSourcesOpen = false,
  showMore = true,
  onSelect,
}: {
  base: string
  primarySources: SourceCardSpec[]
  moreSources: SourceCardSpec[]
  /** Initial disclosure state — the harness pins it to draw the open frame. */
  moreSourcesOpen?: boolean
  /**
   * Hides the disclosure entirely. Prod's cross-post entry point arrives with
   * one network already chosen and shows only that tile, where offering nine
   * alternatives would undo the choice the user just made.
   */
  showMore?: boolean
  onSelect?: (network: SourceNetwork) => void
}) {
  const [moreOpen, setMoreOpen] = React.useState(moreSourcesOpen)

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-6 tw:md:gap-10">
      <div className="tw:grid tw:w-full tw:grid-cols-2 tw:gap-4 tw:md:grid-cols-4">
        {primarySources.map((spec) => (
          <SourceCard
            key={spec.network}
            spec={spec}
            base={base}
            onSelect={onSelect}
          />
        ))}
      </div>

      {showMore ? (
        <>
          {/* py-3 at mobile makes this a 44px target — it's the only route to six
              of the ten sources, so it can't sit at Figma's 36px on a phone. */}
          <button
            type="button"
            className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-2.5 tw:py-3 tw:text-sm tw:font-medium tw:text-foreground tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:md:py-2"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((open) => !open)}
          >
            More sources
            {moreOpen ? (
              <ChevronUp className="tw:size-4" />
            ) : (
              <ChevronDown className="tw:size-4" />
            )}
          </button>

          {moreOpen ? (
            <div className="tw:grid tw:w-full tw:grid-cols-3 tw:gap-3 tw:md:grid-cols-6">
              {moreSources.map((spec) => (
                <MoreSourceCard
                  key={spec.network}
                  spec={spec}
                  base={base}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
